/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  CENTRAL EVENTS REGISTRY — every event MUST be listed here               ║
 * ║                                                                          ║
 * ║  ⚠️  DEVELOPER MANDATORY STEP:                                            ║
 * ║  When you build a NEW event landing page (physical, Zoom, webinar,       ║
 * ║  workshop, meetup, etc.), you MUST add an entry below — otherwise        ║
 * ║  the event will NOT appear on /events and users won't find it from       ║
 * ║  the catalog.                                                            ║
 * ║                                                                          ║
 * ║  See /app/README.md → "Adding a New Event Landing Page" for the full     ║
 * ║  checklist.                                                              ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Schema (per entry):
 *   id:             unique slug (e.g. 'hackathon-june-2026')
 *   title:          Hebrew event title
 *   type:           'physical' | 'zoom' | 'webinar' | 'workshop' | 'meetup'
 *   date:           ISO date 'YYYY-MM-DD' for one-off events, or null for
 *                   recurring (date will be fetched dynamically — see
 *                   `meetingDateEndpoint` below)
 *   time:           'HH:MM' or range like '17:00–21:00'
 *   location:       Free-form Hebrew location (e.g. 'אונליין (Zoom)')
 *   description:    Short Hebrew description shown on the events grid card
 *   spots?:         Capacity (optional)
 *   price?:         { amount: number, currency: 'ILS', label: string } or null for free
 *   isFree:         boolean — controls "חינם" badge
 *   landingUrl:     **Required** — route to the dedicated landing page that
 *                   contains the actual registration form (e.g. '/hackathon').
 *                   The "הרשמה לאירוע" button on /events deep-links here.
 *   ctaLabel?:      Override default CTA text (default: 'הרשמה לאירוע')
 *   meetingDateEndpoint?: For recurring events that only need a dynamic DATE,
 *                   backend endpoint returning `{ meeting_date: ISO }`.
 *                   EventsPage substitutes it into `date`. (Used by PPC Rebel.)
 *   dynamicEventEndpoint?: For events backed by an admin-managed collection,
 *                   backend endpoint returning the FULL event payload —
 *                   date, time, location, price, early-bird metadata.
 *                   Overrides all corresponding static fields on /events
 *                   when the fetch succeeds. (Used by /hackathon.)
 *   isUpcoming:     true if the event is still open for registration; the
 *                   /events page only renders events with isUpcoming=true.
 *
 * Keep entries sorted by date (ascending) so users see the closest events first.
 */
export const EVENTS = [
  {
    id: 'hackathon-june-2026',
    title: 'האקתון: מפיפיסיסט לבעל הון',
    type: 'physical',
    // Static values below are FALLBACKS only — when `dynamicEventEndpoint` is
    // set, EventsPage fetches the live event from the admin-managed collection
    // and overrides date, time, location, and price automatically.
    date: '2026-06-30',
    time: '17:00–21:00',
    location: 'משרדי "שובל בינוי", רחוב הנפח 30, חיפה',
    description:
      'אירוע פיזי בן 4 שעות שבו תיקח את יכולת קניית המדיה שלך ותפנה אותה לבניית נכס דיגיטלי משלך — כולל ארוחת ערב כשרה למהדרין.',
    spots: 60,
    price: { amount: 352.8, currency: 'ILS', label: '299 ש"ח + מע"מ' },
    isFree: false,
    landingUrl: '/hackathon',
    ctaLabel: 'פרטים והרשמה',
    isUpcoming: true,
    // Live admin-managed event source. Returns: event_date, event_time_start,
    // event_time_end, venue, address, price_label, regular_price_label,
    // is_early_bird, early_bird_until, hebrew_date_label.
    dynamicEventEndpoint: '/api/hackathon-events/current',
  },
  {
    id: 'ppc-rebel-zoom',
    title: 'מרד הפיפיסיסטים — מפגש זום סגור',
    type: 'zoom',
    date: null, // fetched dynamically from meetingDateEndpoint
    meetingDateEndpoint: '/api/config/meeting-date',
    time: '21:00',
    location: 'אונליין (Zoom סגור)',
    description:
      'מפגש זום סגור עם רז בליז\'ובסקי — איך הופכים יכולת PPC לנכסים מניבים. הלינק נשלח רק לחברי הקבוצה.',
    price: null,
    isFree: true,
    landingUrl: '/ppc-rebel',
    ctaLabel: 'קבל את הלינק',
    isUpcoming: true,
  },
];

/** Returns true if the event is still open for users to register/attend.
 *  Rules (in order):
 *    1. `isUpcoming === false` → ALWAYS hidden (manual override).
 *    2. Recurring events with no static `date` → assumed upcoming (the next
 *       date is fetched dynamically and is, by definition, in the future).
 *    3. One-off events with a static `date` → only counted if the date is
 *       today or later. Past events are silently dropped from listings + counts.
 */
export const isEventUpcoming = (event) => {
  if (!event || event.isUpcoming === false) return false;
  if (!event.date) return true; // recurring — date is fetched dynamically
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return true; // invalid date → don't drop silently
  return eventDate.getTime() >= today.getTime();
};

/** Upcoming events only — past-dated events are excluded automatically.
 *  Used by /events grid AND by the header badge count. */
export const getUpcomingEvents = () => EVENTS.filter(isEventUpcoming);

/** Returns true if the event happens within the next 7 days (inclusive).
 *  Only applies to events with a static `date` — recurring events (date=null)
 *  return false because we can't determine proximity without fetching the
 *  dynamic meeting date on every page load. */
export const isEventThisWeek = (event) => {
  if (!event || !event.date || !isEventUpcoming(event)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return false;
  const diffDays = (eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 7;
};

/** True if at least one upcoming event happens within the next 7 days. */
export const hasEventThisWeek = () => EVENTS.some(isEventThisWeek);

/** Number of upcoming events. Used by the header CTA badge. */
export const getUpcomingEventsCount = () => getUpcomingEvents().length;

/** Returns number of days from today until the event (0 = today, 1 = tomorrow).
 *  Returns null for past events, recurring events with no static date, or
 *  invalid dates — callers should treat null as "don't show a countdown". */
export const getDaysUntilEvent = (event) => {
  if (!event || !event.date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return null;
  const diffDays = Math.round((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays < 0 ? null : diffDays;
};

/** Returns the next upcoming event by date proximity. Recurring events
 *  (date=null) are deprioritized to the end because their actual date is
 *  unknown without a backend call. Returns null if no upcoming events. */
export const getNextUpcomingEvent = () => {
  const upcoming = getUpcomingEvents();
  if (upcoming.length === 0) return null;
  const sorted = [...upcoming].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  return sorted[0];
};

/** Find event by id (returns null if not found). */
export const getEventById = (id) => EVENTS.find((e) => e.id === id) || null;
